import { useCallback, useState } from 'react';

import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import FilterItemWithCheckbox from 'src/components/Search/Common/Filters/FilterItemWithCheckbox';
import FilterList from 'src/components/Search/Common/Filters/FilterList';
import FilterWrapper from 'src/components/Search/Common/Filters/FilterWrapper';
import LinkMore from 'src/components/Search/Common/Filters/LinkMore';
import { useApplyFilter } from 'src/components/Search/Common/Filters/hooks/useApplyFilter';
import { useFilterGroups } from 'src/components/Search/Common/Filters/hooks/useFilterGroups';
import { toggleValueInArray } from 'src/components/Search/Common/Filters/utils/toggleValueInArray';
import translation from 'src/components/translation';
import { useSelector } from 'src/hooks/useSelector';
import { ClusterGroup, ClusterKey } from 'src/types/search/common/clusters';

const TrlKeys = {
    title: 'clusters.verifiedSkills',
};

const EMPTY_ORDERS = [] as string[];

interface VerifiedSkillsFilterProps {
    sortFunc?: (a: ClusterGroup, b: ClusterGroup) => number;
}

const VerifiedSkillsFilter: TranslatedComponent<VerifiedSkillsFilterProps> = ({ trls, sortFunc }) => {
    const applyFilter = useApplyFilter(ClusterKey.VerifiedSkills);
    const orders = useSelector((state) => state.searchClustersOrder?.[ClusterKey.VerifiedSkills] || EMPTY_ORDERS);
    const searchClusters = useSelector(({ searchClusters }) => searchClusters);
    const { groups, selectedValues } = searchClusters[ClusterKey.VerifiedSkills];

    const [expanded, setExpanded] = useState(false);
    const { items, breakpoint } = useFilterGroups(groups, selectedValues.length, orders, expanded, sortFunc);

    const handleChange = useCallback(
        (item: ClusterGroup) => applyFilter(toggleValueInArray(Number(item.id), selectedValues)),
        [applyFilter, selectedValues]
    );

    if (!items.length) {
        return null;
    }

    return (
        <FilterWrapper filterKey={ClusterKey.VerifiedSkills} title={trls[TrlKeys.title]}>
            <FilterList expanded={expanded} len={items.length}>
                {items.map((item, index) => {
                    if (index > breakpoint - 1 && !expanded) {
                        return null;
                    }
                    return (
                        <FilterItemWithCheckbox
                            key={item.id}
                            item={item}
                            name={ClusterKey.VerifiedSkills}
                            onChange={() => handleChange(item)}
                            checked={selectedValues.includes(+item.id)}
                        />
                    );
                })}
            </FilterList>
            {items.length > breakpoint && (
                <LinkMore
                    length={items.length - breakpoint}
                    expanded={expanded}
                    onClick={() => {
                        setExpanded(!expanded);
                    }}
                />
            )}
        </FilterWrapper>
    );
};

export default translation(VerifiedSkillsFilter);
